import { useState } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsX, BsPencil, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import { ConfirmModal } from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useErrorStore } from '#lib/stores/errorStore';

import Field from '../../components/common/Field/Field';
import { useUpdateBundle, useDeleteBundle } from '../../models/products/mutations';
import { useProductsList, useBundlesList, useBundleDeals } from '../../models/products/queries';
import { formatPrice } from '../../utils/formatters';

// Linked Deals (read-only sidebar)

function LinkedDealsPanel({ bundleId }) {
	const { data: dealsData } = useBundleDeals(bundleId);
	const deals = dealsData?.deals ?? [];
	return (
		<div>
			<h3 className="text-xs font-semibold text-bgBase-800 uppercase tracking-wide mb-2">
				In Deals · {deals.length}
			</h3>
			{deals.length === 0 ? (
				<p className="text-xs text-bgBase-800 py-2">Keinem Deal zugeordnet.</p>
			) : (
				<div className="space-y-1.5">
					{deals.map((d) => (
						<div key={d.id} className="flex items-center justify-between py-1.5 text-xs">
							<div className="min-w-0">
								<div className="font-medium text-text truncate">{d.title}</div>
								<div className="text-bgBase-800">{d.companyNameSnapshot}</div>
							</div>
							<span className="flex-shrink-0 font-bold tabular-nums text-text ml-3">
								{formatPrice(d.dealValue)}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

LinkedDealsPanel.propTypes = { bundleId: PropTypes.string.isRequired };

// Currency Input (shows "3,00 €" when blurred, raw number on focus) ─

function CurrencyInput({ value, onChange, placeholder, className }) {
	const [focused, setFocused] = useState(false);
	const hasValue = value !== null && value !== '';
	const displayValue = focused || !hasValue ? (value ?? '') : formatPrice(Number(value));

	return (
		<TextInput
			type={focused ? 'number' : 'text'}
			min={0}
			step="0.01"
			value={displayValue}
			placeholder={placeholder !== null ? formatPrice(Number(placeholder)) : '0,00 €'}
			onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
			onFocus={() => setFocused(true)}
			onBlur={() => setFocused(false)}
			className={className}
		/>
	);
}

CurrencyInput.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	className: PropTypes.string,
};

// Pricing Mode Toggle

function PricingModeToggle({ mode, onChange }) {
	const options = [
		{ value: 'fixed', label: 'Festpreis' },
		{ value: 'items', label: 'Einzelpreise' },
	];
	return (
		<div className="flex items-center gap-3">
			<span className="text-xs font-medium text-bgBase-800">Preismodell</span>
			<div className="inline-flex rounded-control border border-bgBase-500 bg-bgBase-300 p-0.5">
				{options.map((opt) => (
					<button
						key={opt.value}
						type="button"
						onClick={() => onChange(opt.value)}
						className={`px-3 py-1 text-xs font-medium rounded-control transition-colors ${
							mode === opt.value ? 'bg-white text-primary-700' : 'text-bgBase-800 hover:text-text'
						}`}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	);
}

PricingModeToggle.propTypes = {
	mode: PropTypes.oneOf(['fixed', 'items']).isRequired,
	onChange: PropTypes.func.isRequired,
};

// Bundle Header

function BundleHeader({ bundle, pricingMode, effectivePrice }) {
	return (
		<Card className="mb-3.5 p-0 overflow-hidden">
			<div className="grid items-center gap-4 px-5 py-4" style={{ gridTemplateColumns: '56px 1fr auto' }}>
				<div className="w-14 h-14 rounded-card bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center text-lg font-bold tracking-tight">
					{bundle.name
						?.split(' ')
						.map((n) => n[0])
						.slice(0, 2)
						.join('')
						.toUpperCase() ?? '?'}
				</div>
				<div className="min-w-0">
					<h1 className="text-lg font-bold text-text tracking-tight truncate">{bundle.name}</h1>
					{bundle.description && (
						<div className="mt-1 text-xs text-bgBase-800 truncate">{bundle.description}</div>
					)}
				</div>
				<div className="flex gap-4 lg:gap-7 pl-4 lg:pl-6 border-l border-bgBase-500 flex-shrink-0">
					<div className="flex flex-col gap-1">
						<span className="text-[10px] font-semibold uppercase tracking-wider text-bgBase-800">
							{pricingMode === 'fixed' ? 'Paketpreis' : 'Summe Einzelpreise'}
						</span>
						<span className="text-lg font-bold text-text tabular-nums">
							{formatPrice(effectivePrice) || '–'}
						</span>
					</div>
				</div>
			</div>
		</Card>
	);
}

BundleHeader.propTypes = {
	bundle: PropTypes.object.isRequired,
	pricingMode: PropTypes.string.isRequired,
	effectivePrice: PropTypes.number,
};

// Positions Table

function PositionsTable({ items, products, editing, pricingMode, onUpdateItem, onRemoveItem, onAddItem }) {
	const availableProducts = products.filter((p) => !items.some((it) => it.productId === p.id));
	const isItemPricing = pricingMode === 'items';
	const itemsTotal = items.reduce((acc, it) => {
		const p = products.find((pp) => pp.id === it.productId);
		const unitPrice = isItemPricing && it.priceOverride !== null ? Number(it.priceOverride) : (p?.price ?? 0);
		return acc + unitPrice * (it.quantity ?? 1);
	}, 0);

	return (
		<Card className="p-0 overflow-hidden">
			<div className="px-5 py-3 border-b border-bgBase-400">
				<h3 className="text-xs font-semibold text-bgBase-800 uppercase tracking-wide">
					Positionen · {items.length}
				</h3>
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-bgBase-500 bg-bgBase-300 text-left">
						<th className="px-5 py-2.5 font-medium text-bgBase-800 text-xs">Produkt</th>
						<th className="px-4 py-2.5 font-medium text-bgBase-800 text-xs text-center w-24">Menge</th>
						<th className="px-4 py-2.5 font-medium text-bgBase-800 text-xs text-right w-32">Einzelpreis</th>
						<th className="px-4 py-2.5 font-medium text-bgBase-800 text-xs text-right w-28">Summe</th>
						{editing && <th className="w-10" />}
					</tr>
				</thead>
				<tbody>
					{items.map((it, i) => {
						const p = products.find((pp) => pp.id === it.productId);
						if (!p) return null;
						const hasOverride = isItemPricing && it.priceOverride !== null && it.priceOverride !== '';
						const effectivePrice = hasOverride ? Number(it.priceOverride) : (p.price ?? 0);
						return (
							<tr key={it.productId || i} className="border-b border-bgBase-400 last:border-0">
								<td className="px-5 py-2.5">
									<div className="font-medium text-text">{p.name}</div>
									{p.articleNumber && (
										<div className="text-[11px] text-bgBase-800 font-mono">{p.articleNumber}</div>
									)}
								</td>
								<td className="px-4 py-2.5 text-center">
									{editing ? (
										<TextInput
											type="number"
											min={1}
											value={it.quantity}
											onChange={(e) =>
												onUpdateItem(i, { quantity: parseInt(e.target.value, 10) || 1 })
											}
											className="w-16 text-center"
										/>
									) : (
										it.quantity
									)}
								</td>
								<td className="px-4 py-2.5 text-right">
									{editing && isItemPricing ? (
										<div className="flex flex-col items-end gap-0.5">
											<CurrencyInput
												value={it.priceOverride}
												onChange={(v) => onUpdateItem(i, { priceOverride: v })}
												placeholder={p.price}
												className="w-24 text-right"
											/>
											{hasOverride && p.price !== null && (
												<span className="text-[10px] text-bgBase-800 line-through">
													{formatPrice(p.price)}
												</span>
											)}
										</div>
									) : (
										<div className="flex flex-col items-end">
											<span className={hasOverride ? 'text-text' : 'text-text'}>
												{formatPrice(effectivePrice)}
											</span>
											{hasOverride && p.price !== null && (
												<span className="text-[10px] text-bgBase-800 line-through">
													{formatPrice(p.price)}
												</span>
											)}
										</div>
									)}
								</td>
								<td className="px-4 py-2.5 text-right font-semibold">
									{formatPrice(effectivePrice * (it.quantity ?? 1))}
								</td>
								{editing && (
									<td className="px-2">
										<IconButton
											icon={BsX}
											label="Entfernen"
											tone="danger"
											onClick={() => onRemoveItem(i)}
										/>
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
				<tfoot>
					<tr className="border-t-2 border-bgBase-500">
						<td
							colSpan={editing ? 3 : 3}
							className="px-5 py-2.5 text-right text-xs text-bgBase-800 font-medium"
						>
							Gesamt
						</td>
						<td className="px-4 py-2.5 text-right font-bold text-text">{formatPrice(itemsTotal)}</td>
						{editing && <td />}
					</tr>
				</tfoot>
			</table>
			{editing && (
				<div className="px-5 py-3 border-t border-bgBase-400">
					{availableProducts.length > 0 ? (
						<Select value="" onChange={(e) => onAddItem(e.target.value)} className="max-w-sm">
							<option value="">+ Position hinzufügen …</option>
							{availableProducts.map((p) => (
								<option key={p.id} value={p.id}>
									{p.articleNumber ? `${p.articleNumber} · ` : ''}
									{p.name} · {formatPrice(p.price)}
								</option>
							))}
						</Select>
					) : (
						<p className="text-xs text-bgBase-800">Alle Produkte sind bereits enthalten.</p>
					)}
				</div>
			)}
		</Card>
	);
}

PositionsTable.propTypes = {
	items: PropTypes.array.isRequired,
	products: PropTypes.array.isRequired,
	editing: PropTypes.bool,
	pricingMode: PropTypes.string.isRequired,
	onUpdateItem: PropTypes.func,
	onRemoveItem: PropTypes.func,
	onAddItem: PropTypes.func,
};

// Helpers

function derivePricingMode(bundle) {
	if (bundle.price !== null) return 'fixed';
	return 'items';
}

// Page Component

export default function CrmBundleDetailPage() {
	const { bundleId } = useParams();
	const navigate = useNavigate();

	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState({});
	const [pricingMode, setPricingMode] = useState('items');
	const [deleteOpen, setDeleteOpen] = useState(false);

	const showError = useErrorStore((s) => s.showError);
	const showSuccess = useErrorStore((s) => s.showSuccess);

	const { data: productsData } = useProductsList();
	const { data: bundlesData, isLoading: bundlesLoading } = useBundlesList();

	const products = productsData?.products ?? [];
	const bundles = bundlesData?.bundles ?? [];
	const bundle = bundles.find((b) => b.id === bundleId);

	const updateMut = useUpdateBundle();
	const deleteMut = useDeleteBundle();

	const startEdit = () => {
		const mode = derivePricingMode(bundle);
		setPricingMode(mode);
		setDraft({
			name: bundle.name,
			price: bundle.price ?? '',
			description: bundle.description || '',
			items:
				bundle.items?.map((i) => ({
					productId: i.productId,
					quantity: i.quantity,
					priceOverride: i.priceOverride ?? null,
				})) ?? [],
		});
		setEditing(true);
	};

	const handlePricingModeChange = (newMode) => {
		setPricingMode(newMode);
		if (newMode === 'fixed') {
			// Clear item-level overrides when switching to fixed
			setDraft((d) => ({
				...d,
				items: d.items.map((i) => ({ ...i, priceOverride: null })),
			}));
		} else {
			// Clear fixed price when switching to item pricing
			setDraft((d) => ({ ...d, price: '' }));
		}
	};

	const save = async () => {
		try {
			await updateMut.mutateAsync({
				id: bundle.id,
				payload: {
					name: draft.name,
					price: pricingMode === 'fixed' && draft.price !== '' ? Number(draft.price) : null,
					description: draft.description || null,
					items: draft.items
						.filter((i) => i.productId)
						.map((i) => ({
							productId: i.productId,
							quantity: i.quantity,
							priceOverride:
								pricingMode === 'items' && i.priceOverride !== null && i.priceOverride !== ''
									? Number(i.priceOverride)
									: null,
						})),
				},
			});
			setEditing(false);
			showSuccess({ title: 'Paket gespeichert' });
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Speichern' });
		}
	};

	const handleDelete = async () => {
		try {
			await deleteMut.mutateAsync(bundleId);
			showSuccess({ title: 'Paket gelöscht' });
			navigate('/crm/products');
		} catch (err) {
			showError({ error: err, title: 'Fehler beim Löschen' });
		}
	};

	const updateItem = (idx, patch) => {
		const next = [...draft.items];
		next[idx] = { ...next[idx], ...patch };
		setDraft({ ...draft, items: next });
	};
	const removeItem = (idx) => setDraft({ ...draft, items: draft.items.filter((_, i) => i !== idx) });
	const addItem = (productId) => {
		if (!productId || draft.items.some((it) => it.productId === productId)) return;
		setDraft({ ...draft, items: [...draft.items, { productId, quantity: 1, priceOverride: null }] });
	};

	const v = editing ? draft : (bundle ?? {});
	const currentMode = editing ? pricingMode : derivePricingMode(bundle ?? {});
	const items = editing ? draft.items : (bundle?.items ?? []);
	const itemsTotal = items.reduce((acc, it) => {
		const p = products.find((pp) => pp.id === it.productId);
		const unitPrice =
			currentMode === 'items' && it.priceOverride !== null ? Number(it.priceOverride) : (p?.price ?? 0);
		return acc + unitPrice * (it.quantity ?? 1);
	}, 0);
	const effectivePrice = currentMode === 'fixed' ? Number(v.price) || 0 : itemsTotal;

	if (bundlesLoading)
		return (
			<Page>
				<div className="text-center text-bgBase-800 py-20">Laden...</div>
			</Page>
		);
	if (!bundle) {
		navigate('/crm/products', { replace: true });
		return null;
	}

	return (
		<Page
			header={
				<PageHeader
					breadcrumbs={[
						{ label: 'CRM', to: '/crm' },
						{ label: 'Produkte', to: '/crm/products' },
						bundle.name,
					]}
				>
					<div className="flex items-center gap-2">
						{editing ? (
							<>
								<Button onClick={() => setEditing(false)} variant="outline" size="sm">
									Abbrechen
								</Button>
								<Button onClick={save} size="sm" loading={updateMut.isPending}>
									Speichern
								</Button>
							</>
						) : (
							<>
								<Button onClick={startEdit} variant="outline" size="sm">
									<BsPencil className="w-3 h-3" /> Bearbeiten
								</Button>
								<IconButton
									icon={BsTrash}
									label="Löschen"
									tone="danger"
									onClick={() => setDeleteOpen(true)}
								/>
							</>
						)}
					</div>
				</PageHeader>
			}
		>
			<BundleHeader bundle={bundle} pricingMode={currentMode} effectivePrice={effectivePrice} />

			<div className="pb-8">
				<div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
					<div className="space-y-5">
						<Card className="p-5 space-y-4">
							<Field
								label="Paketname"
								editing={editing}
								value={v.name}
								onChange={(val) => setDraft({ ...draft, name: val })}
								placeholder="z. B. Starter-Bundle"
							/>

							{editing && <PricingModeToggle mode={pricingMode} onChange={handlePricingModeChange} />}

							{currentMode === 'fixed' && (
								<Field
									label="Paketpreis (Festpreis)"
									editing={editing}
									value={v.price}
									onChange={(val) => setDraft({ ...draft, price: val })}
									type="number"
									placeholder="0,00"
								/>
							)}
							{currentMode === 'items' && !editing && (
								<div>
									<label className="block text-xs font-medium text-bgBase-800 mb-1">
										Summe Einzelpreise
									</label>
									<div className="text-sm text-text py-1.5 tabular-nums">
										{formatPrice(itemsTotal)}
									</div>
								</div>
							)}

							<Field
								label="Beschreibung"
								editing={editing}
								value={v.description}
								onChange={(val) => setDraft({ ...draft, description: val })}
								placeholder="Beschreibung …"
								textarea
							/>
						</Card>

						<PositionsTable
							items={items}
							products={products}
							editing={editing}
							pricingMode={currentMode}
							onUpdateItem={updateItem}
							onRemoveItem={removeItem}
							onAddItem={addItem}
						/>
					</div>

					<div className="space-y-6">
						<Card>
							<LinkedDealsPanel bundleId={bundle.id} />
						</Card>
					</div>
				</div>
			</div>

			<ConfirmModal
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={handleDelete}
				title="Paket löschen"
				message={`"${bundle.name}" wirklich löschen?`}
				isLoading={deleteMut.isPending}
			/>
		</Page>
	);
}
